import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { BasicInfoStep } from './components/1-basic-info';
import { DetailsStep } from './components/2-details';
import { UsersStep } from './components/3-team-members';
import { DocumentsStep } from './components/4-documents';
import { StepIndicator } from './components/form/step-indicator';
import { StepNavigation } from './components/form/step-navigation';
import { BasicInfoForm, basicInfoSchema, DetailsForm, detailsSchema } from './schema';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGenerateCaseDetails } from '@/api/mutations/useGenerateCaseDetails';
import useUpdateMatter from '@/api/mutations/useUpdateMatter';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { User } from '@/api/types/backend-dto-types';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { MatterCreationOnboarding } from '@/new/components/onboarding/matter-creation-onboarding';
import { MatterCreationV2Onboarding } from '@/new/components/onboarding/matter-creation-v2-onboarding';
import { PageContainer } from '@/new/components/page-container';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Form } from '@/new/components/shadcn/form';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Permission } from '../../../../../../backend/src/auth/permissions/permission';

const haveSameUserIds = (left: User[], right: User[]) => {
  if (left.length !== right.length) return false;
  const sortIds = (list: User[]) => [...list].map((user) => user.id).sort();
  const leftIds = sortIds(left);
  const rightIds = sortIds(right);
  return leftIds.every((id, index) => id === rightIds[index]);
};

type MatterCreationStep = 'basic' | 'details' | 'users' | 'documents';

export function MatterCreator() {
  const { matterId } = useParams();
  const { hasPermission } = useHasPermission();
  const canCreateUpload = hasPermission(Permission.MatterCreateUpload);
  const navigate = useNavigate();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useState<MatterCreationStep>('basic');
  const [filesProcessing, setFilesProcessing] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [addedUsers, setAddedUsers] = useState<User[]>([]);

  const basicInfoForm = useForm<BasicInfoForm>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      name: '',
      code: '',
      tokenLimit: undefined,
    },
  });
  const detailsForm = useForm<DetailsForm>({
    resolver: zodResolver(detailsSchema),
    defaultValues: {
      issues: '',
      disputeType: '',
      userDefinedEntities: '',
      additionalContext: '',
      timePeriod: '',
      disputeStatus: undefined,
    },
  });

  const { mutateAsync: updateMatter } = useUpdateMatter(matterId ?? '');
  const { isLoading: isLoadingMatter, data: responseMatter } = useGetMatterV2(matterId ?? '');
  const { mutate: generateDetails, isLoading: isGeneratingDetails } = useGenerateCaseDetails();

  const fromChecklistMatterCreation = Boolean(location.state?.fromChecklistMatterCreation);

  if (!matterId) return null;
  if (!canCreateUpload) {
    return <Navigate to="/matters" replace />;
  }

  const handleGenerateDetails = (file: File) => {
    trackCustomEvent(TrackingEventsV2.GenerateMatterDetails, { matterId });
    generateDetails(
      { file },
      {
        onSuccess: (data) => {
          // if succeeded but all fields are null, show error
          if (Object.values(data).every((value) => value === null)) {
            toast.error(
              `Couldn't extract any matter details from the document. Please try again with a different file.`,
            );
            return;
          }

          if (data.caseIssues) detailsForm.setValue('issues', data.caseIssues);
          if (data.caseType) detailsForm.setValue('disputeType', data.caseType);
          if (data.caseEntities) detailsForm.setValue('userDefinedEntities', data.caseEntities);

          toast.success('Matter details extracted successfully!');
        },
        onError: () => {
          toast.error('Failed to extract matter details. Please try again.');
        },
      },
    );
  };

  const handleUserChange = async (users: User[]) => {
    setAddedUsers(users);
    try {
      await updateMatter({ userIds: users.map((x) => x.id) });
      toast.success('Users updated');
    } catch (error) {
      toast.error('Failed to update users');
    }
  };

  // Pre-fill form when data is fetched
  useEffect(() => {
    if (!responseMatter) {
      return;
    }

    basicInfoForm.reset({
      name: responseMatter.name ?? '',
      code: responseMatter.code ?? '',
      tokenLimit: responseMatter.tokenLimit ?? undefined,
    });
    detailsForm.reset({
      issues: responseMatter.issues ?? '',
      disputeType: responseMatter.disputeType ?? '',
      userDefinedEntities: responseMatter.userDefinedEntities ?? '',
      additionalContext: responseMatter.additionalContext ?? '',
      timePeriod: responseMatter.timePeriod ?? '',
      disputeStatus: responseMatter.disputeStatus ?? undefined,
    });
  }, [responseMatter, basicInfoForm, detailsForm]);

  useEffect(() => {
    if (!responseMatter) {
      return;
    }

    const nextUsers = responseMatter.users ?? [];
    setAddedUsers((currentUsers) => {
      if (haveSameUserIds(currentUsers, nextUsers)) {
        return currentUsers;
      }
      return [...nextUsers];
    });
  }, [responseMatter]);

  const handleSubmitBasicInfo = async () => {
    const values = basicInfoForm.getValues();
    trackCustomEvent(TrackingEventsV2.UpdateMatter, { matterId });
    await updateMatter({
      name: values.name,
      code: values.code,
      tokenLimit: values.tokenLimit,
    });
    setCurrentStep('details');
  };

  const handleSubmitDetails = async () => {
    const values = detailsForm.getValues();
    trackCustomEvent(TrackingEventsV2.UpdateMatter, { matterId });
    await updateMatter({
      issues: values.issues,
      disputeType: values.disputeType,
      userDefinedEntities: values.userDefinedEntities,
      additionalContext: values.additionalContext,
      timePeriod: values.timePeriod,
      disputeStatus: values.disputeStatus,
    });
    setCurrentStep('users');
  };

  const handleSubmitUsers = async () => {
    setCurrentStep('documents');
  };

  const handleSubmitDocuments = async () => {
    // If we have uploaded any files then we submit these for processing, else just mark as non draft
    await updateMatter({
      isDraft: false,
    });
    navigate(`/matters/${matterId}`);
  };

  const handleBackStep = (currentStep: MatterCreationStep) => {
    const currentStepIndex = steps.findIndex((s) => s.id === currentStep);
    if (currentStepIndex === 0) {
      navigate(`/matters`);
      return;
    }
    const previousStep = steps[currentStepIndex - 1];
    setCurrentStep(previousStep.id as MatterCreationStep);
  };

  const hasDialogBeenShown = (matterId: string): boolean => {
    const key = `team-members-dialog-shown-${matterId}`;
    return sessionStorage.getItem(key) === 'true';
  };

  const markDialogAsShown = (matterId: string): void => {
    const key = `team-members-dialog-shown-${matterId}`;
    sessionStorage.setItem(key, 'true');
  };

  const handleForwardStep = async (currentStep: MatterCreationStep) => {
    switch (currentStep) {
      case 'basic':
        const isValid = await basicInfoForm.trigger();
        if (isValid) {
          handleSubmitBasicInfo();
        }
        break;
      case 'details':
        const isDetailsValid = await detailsForm.trigger();
        if (isDetailsValid) {
          handleSubmitDetails();
        }
        break;
      case 'users':
        // Check if no additional users have been added and dialog hasn't been shown
        if (addedUsers.length === 1 && !hasDialogBeenShown(matterId)) {
          setConfirmDialogOpen(true);
          return;
        }

        handleSubmitUsers();

        break;
      case 'documents':
        handleSubmitDocuments();
        break;
    }
  };

  const handleConfirmProceed = async () => {
    markDialogAsShown(matterId ?? '');
    setConfirmDialogOpen(false);
    await handleSubmitUsers();
  };

  const steps = [
    { id: 'basic', label: 'Basic Info', icon: 'lucide--info' },
    { id: 'details', label: 'Details', icon: 'lucide--file-text' },
    { id: 'users', label: 'Team Members', icon: 'lucide--users' },
    { id: 'documents', label: 'Documents', icon: 'lucide--upload' },
  ];
  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  if (isLoadingMatter) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={[
          { href: '/matters', label: 'Matters', active: false },
          { href: `/matters/new/${matterId}`, label: 'New Matter', active: true },
        ]}
      />

      <PageContainer className="flex flex-col py-6">
        <div className="mb-8 flex items-center justify-between gap-6">
          <StepIndicator steps={steps} currentStepIndex={currentStepIndex} />
          <StepNavigation
            onBack={() => handleBackStep(currentStep)}
            onNext={() => handleForwardStep(currentStep)}
            nextButtonText={steps[currentStepIndex + 1]?.label ?? ''}
            nextButtonDisabled={filesProcessing || isGeneratingDetails}
          />
        </div>

        <div className="flex h-full min-h-0 flex-1 flex-col">
          {currentStep === 'basic' && (
            <Form {...basicInfoForm}>
              <form onSubmit={basicInfoForm.handleSubmit(handleSubmitBasicInfo)}>
                <div className="flex min-h-0 flex-1 flex-col">
                  <BasicInfoStep form={basicInfoForm} />
                </div>
              </form>
            </Form>
          )}
          {currentStep === 'details' && (
            <Form {...detailsForm}>
              <form onSubmit={detailsForm.handleSubmit(handleSubmitDetails)}>
                <div className="flex min-h-0 flex-1 flex-col">
                  <DetailsStep
                    form={detailsForm}
                    isGeneratingDetails={isGeneratingDetails}
                    onGenerateDetails={handleGenerateDetails}
                  />
                </div>
              </form>
            </Form>
          )}
          {currentStep === 'users' && (
            <div className="flex h-full min-h-0 flex-1 flex-col">
              <UsersStep
                addedUsers={addedUsers}
                onUserChange={handleUserChange}
                matterCreatorId={responseMatter?.createdById}
              />
            </div>
          )}
          {currentStep === 'documents' && (
            <div className="flex h-full min-h-0 flex-1 flex-col">
              <DocumentsStep setFilesProcessing={setFilesProcessing} />
            </div>
          )}
        </div>
      </PageContainer>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Do you want to add team members?</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            You haven't added any additional team members to this matter. Would you like to proceed with just yourself?
          </DialogDescription>
          <DialogFooter className="flex items-center justify-between gap-2">
            <Button
              variant="default"
              onClick={() => {
                setConfirmDialogOpen(false);
              }}
            >
              Add Team Members
            </Button>
            <Button variant="primary" onClick={handleConfirmProceed}>
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {fromChecklistMatterCreation && <MatterCreationOnboarding />}
      <MatterCreationV2Onboarding />
    </div>
  );
}
